import Storage from "./../storage";
import { handleFormSecurity, handleCaptcha } from "./../security";
import { initStripe, handleCardSetup } from "./../stripe";
import serialize from "form-serialize";
import documentReady from "./../helpers/document-ready";
import formResponseHandler from "./../helpers/form-response-handler";
import getQueryParams from "./../helpers/query-params";
import { hyperform } from "./../validation";
import recordLead from "../helpers/intercom";
import isDomainNameUsed from "./../helpers/zoneChecker";

/**
 * Store the original page info
 * to use later on in push state
 *
 * @type       {<type>}
 */
const originalPageInfo = {
    url: location.href,
    title: document.title,
};

let formStep1,
    formStep2,
    formStep3,
    step1Inputs,
    step2Inputs,
    zoneSlug,
    firstnameField,
    lastnameField,
    emailField,
    backButton,
    nextButton,
    nextButton2,
    registerForm,
    submitButton,
    paymentMethodField,
    stripeElement,
    nameOnCardField,
    isSubmitting,
    defaultPlan,
    planSwitcher,
    selectedPlan;

const selectElements = () => {
    formStep1 = document.getElementById("c-register--step1");
    formStep2 = document.getElementById("c-register--step2");
    formStep3 = document.getElementById("c-register--step3");
    step1Inputs = formStep1.getElementsByTagName("input");
    step2Inputs = formStep2.getElementsByTagName("input");
    zoneSlug = document.getElementsByName("fos_user_registration_form[advertiser][zone][slug]")[0];
    firstnameField = document.getElementsByName("fos_user_registration_form[firstname]")[0];
    lastnameField = document.getElementsByName("fos_user_registration_form[lastname]")[0];
    emailField = document.getElementsByName("fos_user_registration_form[email]")[0];
    backButton = document.getElementById("c-register__back-button");
    nextButton = document.getElementById("c-register__next-button");
    nextButton2 = document.getElementById("c-register__next-button2");
    submitButton = document.getElementById("c-register__submit-button");
    registerForm = document.getElementById("register-form");
    nameOnCardField = document.getElementsByName("name_on_card")[0];
    paymentMethodField = document.querySelector(
        '[name="fos_user_registration_form[paymentData][paymentMethod]"]:checked',
    );
    planSwitcher = document.querySelectorAll('.registration-form__right .plans-switcher input[type="checkbox"]')[0];
};

const setErrorsOnInvalidInputsFirst = () => {
    Array.from(step1Inputs).forEach((input) => {
        input.reportValidity();
    });
};

const setErrorsOnInvalidInputsSecond = () => {
    Array.from(step2Inputs).forEach((input) => {
        input.reportValidity();
    });
};

/**
 * Checks if the inputs in step 1 are valid
 * and stores input values
 */
const firstStepValid = () => {
    const invalidInputs = Array.from(step1Inputs).filter((input) => {
        input.classList.add("is-user-invalid");
        return !input.checkValidity();
    });

    // True if no invalid elements
    if (invalidInputs.length === 0) {
        // Remove disabled class from button
        nextButton.classList.remove("c-button--disabled");
        return true;
    }
    // Invalid input
    nextButton.classList.add("c-button--disabled");
    return false;
};

const secondStepValid = async () => {
    zoneSlug.setCustomValidity('');

    let isValid = zoneSlug.checkValidity();

    if (false === isValid) {
        let validity = zoneSlug.validity;

        switch (true) {
            case validity.badInput:
                zoneSlug.setCustomValidity('You can only use: a-z A-Z 0-9. Hyphens (\"-\") can be used if surrounded by other characters, for example \"My-Business\".')
                break;
            case validity.patternMismatch:
                zoneSlug.setCustomValidity('Only (lowercase) letters and numbers (a-z & 0-9) are allowed.')
                break;
            case validity.tooShort:
                zoneSlug.setCustomValidity('Subdomain must be at least 3 characters long.')
                break;
            case validity.valueMissing:
                zoneSlug.setCustomValidity('Please fill out this field.')
                break;
            default:
                zoneSlug.setCustomValidity('')
        }
        disableNextButton2();

        setErrorsOnInvalidInputsSecond();

        return isValid;
    }

    isValid = await checkFreeZoneName();

    return isValid;
}

/**
 * Checks if the inputs in step 3 are valid
 * and stores input values
 *
 * @param      {<type>}  id      The identifier
 * @return     {<type>}  { description_of_the_return_value }
 */
const thirdStepValid = () => {
    const invalidInputs = Array.from(document.getElementsByName("name_on_card")).filter((input) => {
        input.classList.add("is-user-invalid");
        return !input.checkValidity();
    });

    let paymentMethod = document.querySelector(
        `[name='fos_user_registration_form[paymentData][paymentMethod]']:checked`,
    ).value;

    // True if no invalid elements or paypal (No inputs when paypal)
    if (invalidInputs.length === 0 || paymentMethod === "paypal") {
        // Remove disabled class from button
        submitButton.classList.remove("c-button--disabled");
        return true;
    }
    // Invalid input
    submitButton.classList.add("c-button--disabled");
    return false;
};

/**
 * Stores form values.
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const storeFormValues = () => {
    const formElements = registerForm.elements;
    return Array.from(formElements).forEach((element) => {
        // Don't store password
        if (element.type === "password") {
            return;
        }
        if (element.type === "radio") {
            const value = Array.from(document.getElementsByName(element.name)).find(
                (r) => r.checked,
            ).value;
            return Storage.setItem(element.name, value);
        }
        return Storage.setItem(element.name, element.value);
    });
};

/**
 * Retrieves stored form values.
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const retrieveStoredFormValues = () => {
    const formElements = registerForm.elements;
    return Array.from(formElements).forEach((element) => {
        const storedValue = Storage.getItem(element.name);
        if (!storedValue) {
            return;
        }

        const isPaymentMethod = element.name === "payment-method";
        if (isPaymentMethod) {
            switchPaymentMethod(element.value);
        }

        switch (element.type) {
            case "radio":
                const radioButton = document.querySelector(
                    `input[name='${element.name}'][value=${storedValue}]`,
                );

                if (!radioButton) return;

                radioButton.checked = true;
                break;

            case "select-one":
                const option = document.querySelector(
                    `[name='${element.name}'] option[value='${storedValue}']`,
                );

                if (!option) return;

                option.selected = "selected";
                break;

            default:
                element.classList.add("is-valid");
                element.value = storedValue;
                break;
        }
    });
};

/**
 * Pushes a history state.
 *
 * @param      {<type>}  _step   The step
 * @return     {<type>}  { description_of_the_return_value }
 */
const pushState = (_step) => {
    // Make sure step is not a string
    const step = parseInt(_step);
    let pagePath = "/signup/";
    let pageTitle = "Tapfiliate.com - Signup";
    if (step === 0) {
        pagePath = originalPageInfo.url;
        pageTitle = originalPageInfo.title;
    }
    const pathname = window.location.pathname;

    if (step === 1) {
    }
    if (step === 2) {
        if (!firstStepValid()) {
            setErrorsOnInvalidInputsFirst();
            return;
        }
        pagePath = "/signup/#step-2";
        removeBloks(pathname);
    }
    if (step === 3) {
        pagePath = "/signup/#step-3";
        removeBloks(pathname);
    }

    if (pathname === "/software/" || pathname === "/free-trial/") {
        window.scrollTo(0, 0);
    }

    history.pushState({}, pageTitle, pagePath);

    window.dataLayer.push({
        event: "spaPageView",
    });
};

/**
 * Removes blocks with text
 *
 * @param      {<type>}  pathname   The pathname
 */
const removeBloks = (pathname) => {
    if (pathname === "/software/") {
        const softwarePage = document.getElementById("c-software-page");
        const logoDiv = document.getElementsByClassName("c-logo")[0];
        softwarePage.classList.add("c-software-page__signup");
        logoDiv.innerHTML = "<img src='https://tapfiliate.com/img/logo.svg' id='logo-file'>";
    }
    if (pathname === "/free-trial/") {
        const trialPage = document.getElementById("c-free-trial");
        trialPage.classList.add("c-free-trial__signup");
    }
}

/**
 * Goes to a specific step in the registration process
 *
 * @param      {int}  _step   The step
 * @param      {boolean}  _step   The step
 * @return     {int}  { description_of_the_return_value }
 */
const goToStep = async (_step, sendPushToGA = true) => {
    storeFormValues();
    // Make sure step is not a string
    const step = parseInt(_step);

    if (step === 1) {
        // Go back
        formStep1.classList.add("is-active");
        formStep2.classList.remove("is-active");
        formStep3.classList.remove("is-active");
        if (window.location.pathname !== "/software/") {
            firstnameField.focus();
        }
        backButton?.setAttribute("data-register-step", 0);
        nextButton?.setAttribute("data-register-step", 2);
        submitButton?.classList.add("u-display-none");
        nextButton?.classList.remove("u-display-none");
        document.getElementById("step-3-info")?.classList.add("u-display-none");
        document.getElementById("step-1-info")?.classList.remove("u-display-none");

        // TODO TF-783: Delete after task complete
        const nodes = document.getElementsByClassName("test-register-plan-switcher");
        for (let node of nodes) {
            node.classList.remove("u-display-none");
        }
        // const nodes = document.getElementsByClassName("test-register-plan-switcher");
        // for (let node of nodes) {
        //     node.classList.add("u-display-none");
        // }
    }

    if (step === 2) {
        if (!firstStepValid()) {
            return goToStep(1, false);
        }

        recordLeadAttribute("lead-from-signup-step-1");

        zoneSlug.focus();
        formStep1.classList.remove("is-active");
        formStep2.classList.add("is-active");
        formStep3.classList.remove("is-active");
        backButton?.setAttribute("data-register-step", 1);
        nextButton2?.setAttribute("data-register-step", 3);
        nextButton2?.classList.add("u-display-none");

        // TODO TF-783: Delete after task complete
        submitButton?.classList.remove("u-display-none");
        // submitButton?.classList.add("u-display-none");

        nextButton2?.classList.remove("u-display-none");
        if (backButton) {
            backButton.dataset.registerStep = 1;
        }

        document.getElementById("step-3-info")?.classList.add("u-display-none");
        document.getElementById("step-1-info")?.classList.remove("u-display-none");

        const nodes = document.getElementsByClassName("test-register-plan-switcher");
        for (let node of nodes) {
            node.classList.add("u-display-none");
        }
    }
    if (step === 3) {
        if (!await secondStepValid()) {
            return goToStep(2, false);
        }

        recordLeadAttribute("lead-from-signup-subdomain-chooser");

        formStep1.classList.remove("is-active");
        formStep2.classList.remove("is-active");
        formStep3.classList.add("is-active");
        backButton?.setAttribute("data-register-step", 1);
        nextButton?.classList.add("u-display-none");
        nextButton2?.classList.add("u-display-none");
        submitButton?.classList.remove("u-display-none");
        if (backButton) {
            backButton.dataset.registerStep = 2;
        }
        document?.getElementById("step-1-info")?.classList.add("u-display-none");
        document?.getElementById("step-3-info")?.classList.remove("u-display-none");
        nameOnCardField.focus();
        const nodes = document.getElementsByClassName("test-register-plan-switcher");
        for (let node of nodes) {
            node.classList.remove("u-display-none");
        }
        switchPaymentMethod();

        window.dataLayer.push({
            event: "signupCompletedBasicDetailsStep",
            email: emailField.value,
            name: firstnameField.value + " " + lastnameField.value,
        });
    }

    return _step;
};

/**
 * Add event listeners to the buttons that handle the registration steps
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const registerStepButtons = () => {
    const stepButtons = document.querySelectorAll("[data-register-step]");

    return stepButtons.forEach((stepButton) =>
        stepButton.addEventListener("click", async (event) => {
            event.preventDefault();
            const targetStep = event.currentTarget.getAttribute("data-register-step");
            let step = await goToStep(targetStep);

            if (targetStep === step) {
                pushState(step);
            }

            return false;
        }),
    );
};

/**
 * Logic for switching payment methods
 *
 * @param      {string}  method  The method
 * @return     {<type>}  { description_of_the_return_value }
 */
const switchPaymentMethod = (method) => {
    method =
        method === undefined
            ? Storage.getItem("fos_user_registration_form[paymentData][paymentMethod]") ||
              "credit-card"
            : method;
    const creditCardForm = document.getElementById("c-form__credit-card");
    creditCardForm.style.display = method === "credit-card" ? "block" : "none";

    const paypalForm = document.getElementById("c-form__paypal");
    paypalForm.style.display = method === "paypal" ? "block" : "none";

    if (stripeElement && method === "credit-card") {
        window.scrollTo(0, 0);
        nameOnCardField.focus();
    }
};

/**
 * Add event listeners when we change payment options
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const registerPaymentOptions = () => {
    const paymentMethodRadioboxes = document.getElementsByClassName("c-register__payment-radio");

    return Array.from(paymentMethodRadioboxes).forEach((radioButton) =>
        radioButton.addEventListener("change", (event) => switchPaymentMethod(event.target.value)),
    );
};

/**
 * Updates the trial end date to the current date
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const updateTrialEndDate = () => {
    const daysInTrial = parseInt(selectedPlan.dataset.trialDays);

    function addDays(date, days) {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const endDate = addDays(new Date(), daysInTrial);

    const year = endDate.getFullYear();
    const day = endDate.getDate();
    const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const month = endDate.getMonth();

    const trialHolders = document.getElementsByClassName("trial-end-date");
    Array.from(trialHolders).forEach(
        (trial) => (trial.innerHTML = `${monthNames[month]} ${day}, ${year}`),
    );
};

const filterPlansSelect = async function (checked) {
    const cycle = checked ? 'yearly' : 'monthly';
    const plans = document.querySelectorAll(`#selected-plan option`);

    plans.forEach((option) => {
        option.disabled = option.dataset.cycle !== cycle;
    });
};

const resetPlansSelect = async function () {
    let option;
    let selector = document.getElementById("selected-plan");
    let selectedOption = selector.options[selector.selectedIndex];

    selector.selectedIndex = 0;

    for (var i = 0; i < selector.options.length; i++) {
        option = selector.options[i];
        if (option.dataset.id !== selectedOption.dataset.id && option.dataset.slug == selectedOption.dataset.slug) {
            selector.selectedIndex = i;
            changePlanDescription(option);
        }
    }
};

const changePlanDescription = function (selectedPlan) {
    const planName = document.getElementsByClassName("selected-plan-name");
    const planPrice = document.getElementsByClassName("selected-plan-price");
    const planTrialDays = document.getElementsByClassName("selected-plan-trial-days");

    Array.from(planName).forEach(
        (planNameToChange) => (planNameToChange.innerHTML = selectedPlan.dataset.title),
    );

    Array.from(planPrice).forEach(
        (planPriceToChange) => (planPriceToChange.innerHTML = selectedPlan.dataset.price),
    );

    Array.from(planTrialDays).forEach(
        (planTrialDaysToChange) =>
            (planTrialDaysToChange.innerHTML = selectedPlan.dataset.trialDays),
    );
};

/**
 * Switch to another plan programmatically
 */
const switchPlan = async function (plan) {
    const plans = document.querySelectorAll(`#selected-plan option`);

    const existingPlanIds = [];
    plans.forEach((option) => {
        existingPlanIds.push(option.dataset.id);
        if (option.dataset.id === plan) {
            selectedPlan = option;
        }
        if (option.dataset.default === "true") {
            defaultPlan = option;
        }
    });

    // Fetch plan if not already in options
    if (plan && plan !== "null" && !existingPlanIds.includes(plan)) {
        const response = await fetch(APP_DOMAIN + `/plans/${plan}/`);

        try {
            const data = await response.json();

            if (!existingPlanIds.includes(data.idsc)) {
                const option = document.createElement("option");
                option.dataset.title = data.title;
                option.dataset.price = data.price;
                option.dataset.trialDays = data.trial_days;
                option.dataset.id = option.value = data.idsc;
                option.selected = true;
                option.text = `${data.title} (\$${data.price} p/m)`;

                const planSelector = document.querySelector(`#selected-plan`);
                planSelector.add(option);
                selectedPlan = option;
            }
        } catch(e) {}
    }

    selectedPlan = selectedPlan || defaultPlan || plans[0];

    if (!selectedPlan) return;

    selectedPlan.selected = true;

    selectedPlan.dataset.cycle === 'yearly' ? planSwitcher.checked = true : planSwitcher.checked = false;

    await filterPlansSelect(planSwitcher.checked);

    changePlanDescription(selectedPlan);

    if (plan === "default") return;

    return Storage.setItem("selected-plan", plan);
};

const addSubmitListener = () => {
    const form = document.getElementById("register-form");

    form.addEventListener("submit", async (ev) => {
        ev.preventDefault();
        storeFormValues();

        // TODO TF-783: Delete if you want to rollback changes
        setSubmitButtonLoading();

        if (!await secondStepValid()) {
            setSubmitButtonLoading(false);

            return goToStep(2, false);
        }

        registerStripe(form);

        // let paymentMethod = document.querySelector(
        //     `[name='fos_user_registration_form[paymentData][paymentMethod]']:checked`,
        // ).value;
        // switch (paymentMethod) {
        //     case "credit-card":
        //         registerStripe(form);
        //         break;
        //     case "paypal":
        //         doRegister(form, 'paypal');
        //         break;
        // }
    });
};

const setSubmitButtonLoading = (isLoading = true) => {
    if (isLoading) {
        submitButton.classList.add("c-button--loading");
        submitButton.setAttribute("disabled", "");
    } else {
        submitButton.classList.remove("c-button--loading");
        submitButton.removeAttribute("disabled");
    }
};

const registerStripe = (form) => {

    // TODO TF-783: Uncomment if you want to rollback changes
    // const nameOnCard = nameOnCardField.value;
    // const email = emailField.value;

    setSubmitButtonLoading();

    // TODO TF-783: Delete if you want to rollback changes
    return doRegister(form, 'credit card');

    // Payment intent already handled
    if (
        document.getElementsByName("fos_user_registration_form[paymentData][customerId]")[0].value
    ) {
        return doRegister(form, 'credit card');
    }

    handleCardSetup(nameOnCard, email).then(function (result) {
        if (result.error) {
            setSubmitButtonLoading(false);
            var errorElement = document.getElementById("card-errors");
            errorElement.textContent = result.error.message;

            recordLeadAttribute("lead-from-signup-payment-error");
        } else {
            document.getElementsByName(
                "fos_user_registration_form[paymentData][customerId]",
            )[0].value = result.setupIntent.payment_method;
            recordLeadAttribute("lead-from-signup");

            return doRegister(form, 'credit card');
        }
    });
};

const doRegister = (form, type) => {
    if (isSubmitting) return;

    isSubmitting = true;
    setSubmitButtonLoading();
    return fetch(APP_DOMAIN + "/signup/", {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: serialize(form),
        credentials: "include",
    })
        .then((result) => {
            return result;
        })
        .then(formResponseHandler)
        .then(() => {
            handleFormSecurity(
                "fos_user_registration_form[_token]",
                "fos_user_registration_form",
            ).then(() => {
                isSubmitting = false;
                setSubmitButtonLoading(false);
                if (!firstStepValid()) {
                    return goToStep(1, false);
                }
            });
        });
};

const recordLeadAttribute = (leadAttribute) => {
    const form = document.getElementById("register-form");
    const leadData = {
        email: form.querySelector('[name="fos_user_registration_form[email]"]').value,
        firstname: form.querySelector('[name="fos_user_registration_form[firstname]"]').value,
        lastname: form.querySelector('[name="fos_user_registration_form[lastname]"]').value,
        csrfToken: form.querySelector('[name="fos_user_registration_form[_token]"]').value,
    };

    recordLead(leadData, leadAttribute);
};

const setupValidation = () => {
    ["keyup", "click"].forEach((event) => registerForm.addEventListener(event, firstStepValid));
    ["keyup", "change"].forEach((event) => zoneSlug.addEventListener(event, enableNextButton2));

    // TODO TF-783: Uncomment if you want to rollback changes;
    // ["keyup", "click"].forEach((event) => registerForm.addEventListener(event, thirdStepValid));
};

/**
 * Listens to plan changes of the select drop down and stores them
 *
 * @return     {<type>}  { description_of_the_return_value }
 */
const planListener = () =>
    document.getElementById("selected-plan").addEventListener("change", (event) => {
        switchPlan(event.target.options[event.target.selectedIndex].dataset.id);
    });

const planCycleListener = () =>
    planSwitcher.addEventListener("change", (event) => {
        filterPlansSelect(event.target.checked);
        resetPlansSelect();
    });

window.onpopstate = () => {
    if (window.location.pathname === "/signup/") {
        const step = window.location.hash === "#step-3" ? 3 : window.location.hash === "#step-2" ? 2 : 1;
        goToStep(step);
    }
};

/**
 * Add data register button listeners
 *
 * @return     {<type>}  { description_of_the_return_value }
 */

let booted = false;
const boot = () => {
    if (booted) return;
    booted = true;
};

document.addEventListener("DOMContentLoaded", boot);

// Somehow Safari never emits DOMContentLoaded for us
// TODO: Invesstigate why this is
window.onload = boot;

/**
 * Activates the registration
 *
 * @param      {string}  plan    The plan
 * @return     {<type>}  { description_of_the_return_value }
 */
const activate = async function (plan, requestedStep, storeState = true) {
    documentReady(async function () {
        hyperform.addTranslation("en", {
            ValueMissing: "This field can not be empty",
            CheckboxMissing: "You need to agree to our policies to proceed",
            InvalidEmail: "Please enter a valid email address",
            // hyperform bug for TextToShort but this solves the problem
            "Please lengthen this text to %l characters or more (you are currently using %l characters).":
                "The password you entered is too short",
        });

        if (IS_PRODUCTION) {
            handleCaptcha();
        }
        selectElements();
        registerPaymentOptions();
        registerStepButtons();
        planCycleListener();
        planListener();
        retrieveStoredFormValues();

        const thePlan = getQueryParams().pid || plan;

        await switchPlan(thePlan);
        updateTrialEndDate();

        // Default to go to step 1
        let stepTogoTo = requestedStep ? requestedStep : 1;

        if (storeState) {
            pushState(stepTogoTo);
        }

        const withSetupToken = true;

        // TODO TF-783: Uncomment if you want to rollback changes;
        // initStripe(withSetupToken).then((el) => {
        //     stripeElement = el;
        // });

        switchPaymentMethod();

        handleFormSecurity("fos_user_registration_form[_token]", "fos_user_registration_form");

        addSubmitListener();
        setupValidation();

        return goToStep(stepTogoTo);
    });
};

const checkFreeZoneName = async function () {
    disableNextButton2();

    let domainExist = await isDomainNameUsed(zoneSlug.value, (error) => {
        zoneSlug.setCustomValidity(error);
        setErrorsOnInvalidInputsSecond();
        enableNextButton2();
    });

    await handleFormSecurity("fos_user_registration_form[_token]", "fos_user_registration_form");

    if (domainExist) {
        zoneSlug.setCustomValidity('This name is already in use. Please try an alternative one.');
        setErrorsOnInvalidInputsSecond();
    } else {
        enableNextButton2();
        zoneSlug.setCustomValidity('');
    }

    return !domainExist;
}

const disableNextButton2 = function () {
    if (nextButton2) {
        nextButton2.classList.add("c-button--disabled");
        nextButton2.disabled = true;
    }
}

const enableNextButton2 = function() {
    if (nextButton2) {
        nextButton2.classList.remove("c-button--disabled");
        nextButton2.disabled = false;
    }
}

activate(Storage.getItem("selected-plan"), 1, false);

export default {
    activate,
};
